import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, SilentRequest } from '@azure/msal-browser';
import { ReplaySubject, Subject, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import User from 'src/models/user';
import { UserPreferencesService } from './user-preferences.service';
const downLoadEnabeledUsers = ['mbidzo.ladzani@unilever.com','govind.rawat@unilever.com','mohammed.cp@unilever.com'];
@Injectable({
  providedIn: 'root'
})
export class UserSessionService {
  private user: User | null = null;
  private msalEvents: Subscription | null = null;
  private internalAccessToken: string | null = null;

  constructor(
    private preferences: UserPreferencesService,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
  ) {
  }

  get displayName(): string { return this.user?.displayName || 'not logged in'; }
  get username(): string { return this.user?.username || ''; }
  get photoUrl(): string | null { return this.user?.photoUrl || null; }
  get isLoggedIn(): boolean { return this.user !== null; }
  private get isAuthorised(): boolean { return this.internalAccessToken !== null; }

  // Default to Port Sunlight Line 6
  get activeLineId(): string { return this.preferences.activeLineId || '0a104578-2a02-47d1-aa0b-7d22b7f25375' }
  set activeLineId(value: string) {
    const oldValue = this.activeLineId;
    this.preferences.activeLineId = value;
    if (value && (oldValue !== value)) {
      this.activeLineIdChanged.next(value);
    }
  }
  get accessToken(): string | null { return this.internalAccessToken; }
  get excelDownLoadEnabled():boolean { return downLoadEnabeledUsers.includes(this.username.toLowerCase());}
  isLoggedInChanged: Subject<boolean> = new ReplaySubject<boolean>(1);
  // This observable is required for consumers that don't use Angular's HTTP Client
  // (and therefore don't benefit from Angular MSAL's auto-authorisation mechanism).
  isAuthorisedChanged: Subject<boolean> = new ReplaySubject<boolean>(1);
  activeLineIdChanged: Subject<string> = new ReplaySubject<string>(1);

  init(): void {
    if (!environment.isLocalDev) {
      this.msalEvents = this.msalBroadcastService.msalSubject$.subscribe(this.msalEventHandler);
      this.refreshAuthenticatedUser();
    } else {
      this.user = {
        displayName: 'Local Dev',
        username: 'local.dev',
        accountInfo: {} as any
      };
      this.internalAccessToken = "stuff";
      this.isLoggedInChanged.next(true);
      this.isAuthorisedChanged.next(true);
    }
  }

  destroy(): void {
    this.msalEvents?.unsubscribe();
  }

  signIn(): void {
    if (!this.isLoggedIn) {
      this.msalService.loginPopup();
    }
  }

  signOut(): void {
    this.msalService.logout();
  }

  async refreshAuthorisation(): Promise<void> {
    const request: SilentRequest = {
      scopes: Array.from(environment.msalInterceptorConfig.protectedResourceMap.entries()).flatMap(entry => entry[1] as string[]),
      account: this.user?.accountInfo
    };
    let response: AuthenticationResult | null = null;
    try {
      response = await this.msalService.acquireTokenSilent(request).toPromise();
    } catch {
      response = await this.msalService.acquireTokenPopup(request).toPromise();
    }

    if (response) {
      this.updateAccessToken(response.accessToken);
    }
  }

  private msalEventHandler = (event: EventMessage) => {
    const relevantLoginEvents = [EventType.LOGIN_SUCCESS, EventType.LOGOUT_SUCCESS];
    const relevantAccessTokenEvents = [EventType.ACQUIRE_TOKEN_SUCCESS];

    const isLoginEvent = relevantLoginEvents.includes(event.eventType);
    const isAccessTokenEvent = relevantAccessTokenEvents.includes(event.eventType);

    if (isLoginEvent) {
      this.refreshAuthenticatedUser();
    }

    if (isAccessTokenEvent) {
      this.updateAccessToken((event.payload as AuthenticationResult).accessToken);
    }
  }

  private refreshAuthenticatedUser(): void {
    const wasLoggedIn = this.isLoggedIn;
    const authenticatedUser = this.msalService.instance.getAllAccounts()[0] || null;

    this.user = authenticatedUser && {
      displayName: authenticatedUser.name || authenticatedUser.username,
      username: authenticatedUser.username,
      accountInfo: authenticatedUser,
    };

    if (wasLoggedIn !== this.isLoggedIn) {
      this.isLoggedInChanged.next(this.isLoggedIn);
    }
  }

  private updateAccessToken(value: string | null) {
    const wasAuthorised = this.isAuthorised;

    const tokenChanged = value !== this.internalAccessToken;
    this.internalAccessToken = value;
    
    const isAuthorisedChanged = this.isAuthorised !== wasAuthorised;

    const notificationRequired = tokenChanged || isAuthorisedChanged;
    if (notificationRequired) {
      this.isAuthorisedChanged.next(this.isAuthorised);
    }
  }
}
